import { Link } from 'react-router-dom';
import { services } from '@/mocks/tmtData';

export default function ServicesPage() {
  return (
    <div className="bg-background-50/95">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
            / What We Offer
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight max-w-3xl mb-6">
            Complete Digital{' '}
            <span className="text-primary-400">Financial Suite</span>
          </h1>
          <p className="text-foreground-300 text-base md:text-lg leading-relaxed max-w-2xl mb-10 font-body">
            From everyday payments to AI-powered financial intelligence, TMT provides a comprehensive
            range of services designed for the modern digital economy — all from one unified platform.
          </p>

          {/* Hero Banner Image */}
          <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/50 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Premium%20digital%20financial%20ecosystem%20visualization%20multiple%20floating%20smartphone%20screens%20showing%20different%20payment%20services%20dark%20background%20with%20golden%20light%20trails%20connecting%20them%20unified%20fintech%20platform%20concept&width=1400&height=500&seq=services-hero-7&orientation=landscape"
              alt="TMT Complete Digital Financial Suite"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
            / Platform
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-10">
            Core Services
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                className="group relative bg-background-50/90 border border-primary-800/20 rounded-xl overflow-hidden hover:border-primary-600/40 transition-all duration-500 block cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-50 to-transparent" />
                  <div className="absolute top-4 left-4 w-10 h-10 bg-primary-500/20 backdrop-blur-sm border border-primary-500/30 rounded-lg flex items-center justify-center">
                    <i className={`${service.icon} text-primary-400 text-lg`} />
                  </div>
                </div>
                {/* Content */}
                <div className="p-5 md:p-6">
                  <h3 className="font-heading text-lg md:text-xl text-foreground-100 mb-3 group-hover:text-primary-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-foreground-400 text-sm leading-relaxed font-body line-clamp-3">
                    {service.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Getting Started
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              {
                step: '01',
                title: 'Create Account',
                desc: 'Sign up in minutes with your phone number. Verify your identity securely through our AI-powered KYC system.',
                icon: 'ri-user-add-line',
              },
              {
                step: '02',
                title: 'Add Funds',
                desc: 'Link your bank account, add a card, or deposit cash through our authorized partner network to fund your wallet instantly.',
                icon: 'ri-wallet-3-line',
              },
              {
                step: '03',
                title: 'Start Transacting',
                desc: 'Send money, pay bills, shop online, or use your digital Visa card — all from one powerful app.',
                icon: 'ri-send-plane-line',
              },
            ].map((item, index) => (
              <div key={item.step} className="relative text-center">
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary-700/40 to-transparent" />
                )}
                <div className="w-24 h-24 mx-auto mb-6 bg-background-50/90 border border-primary-700/30 rounded-full flex items-center justify-center relative">
                  <i className={`${item.icon} text-primary-400 text-2xl`} />
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-primary-500 text-background-50 rounded-full flex items-center justify-center font-label text-xs font-bold">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading text-xl text-foreground-100 mb-3">{item.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed max-w-xs mx-auto font-body">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Developer API */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / For Developers
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 max-w-2xl">
              Developer API & E-Commerce Integration
            </h2>
            <p className="text-foreground-300 text-base leading-relaxed max-w-2xl mt-3 font-body">
              Embed TMT payments directly into your platform with our RESTful API. Built for developers who demand speed, clarity, and production-grade reliability.
            </p>
          </div>

          {/* Feature List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                icon: 'ri-code-box-line',
                title: 'RESTful Architecture',
                desc: 'Clean, predictable endpoints with consistent JSON responses. Every resource is addressable via intuitive URL patterns — no surprises, just standards-compliant REST.',
              },
              {
                icon: 'ri-terminal-box-line',
                title: 'SDKs for Every Stack',
                desc: 'First-class client libraries for Node.js, Python, PHP, Ruby, Java, and Go. Drop-in integration with minimal configuration and full TypeScript support out of the box.',
              },
              {
                icon: 'ri-webhook-line',
                title: 'Webhooks & Event Streaming',
                desc: 'Receive real-time notifications for payment confirmations, refunds, disputes, and account updates. Configure retry policies, signature verification, and idempotency keys for guaranteed delivery.',
              },
              {
                icon: 'ri-shield-keyhole-line',
                title: 'Tokenized Card Vault',
                desc: 'Securely store and charge cards without handling raw PAN data. Our PCI-DSS Level 1 compliant vault returns reusable tokens you can safely persist in your own database.',
              },
              {
                icon: 'ri-flask-line',
                title: 'Full Sandbox Environment',
                desc: 'Test every endpoint against a live sandbox with simulated responses, configurable failure modes, and webhook inspection tools before deploying a single line to production.',
              },
              {
                icon: 'ri-file-text-line',
                title: 'Interactive API Reference',
                desc: 'Browse every endpoint, parameter, and response schema in our OpenAPI 3.1 spec. Use the built-in API explorer to make live test calls directly from the documentation.',
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="flex gap-4 p-4 rounded-lg hover:bg-background-50/60 transition-colors group"
              >
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-primary-600/30 rounded-lg bg-background-50/90 group-hover:border-primary-400 group-hover:bg-primary-500/10 transition-colors">
                  <i className={`${feature.icon} text-primary-400 text-lg`} />
                </div>
                <div>
                  <h4 className="font-heading text-foreground-100 text-sm md:text-base font-semibold mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-foreground-400 text-xs md:text-sm leading-relaxed font-body">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-400 mb-4">
            Ready to Experience TMT?
          </h2>
          <p className="text-foreground-300 text-base leading-relaxed mb-8 font-body">
            Download the TMT Digital Application and start enjoying seamless financial services today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#"
              className="flex items-center gap-3 px-6 py-3 bg-background-100/90 border border-primary-700/30 rounded-xl hover:border-primary-500/50 transition-colors"
            >
              <i className="ri-apple-fill text-foreground-100 text-2xl" />
              <div className="text-left">
                <p className="text-foreground-500 text-[0.6rem] font-label leading-none">Download on the</p>
                <p className="text-foreground-100 text-sm font-label font-semibold leading-tight">App Store</p>
              </div>
            </a>
            <a
              href="#"
              className="flex items-center gap-3 px-6 py-3 bg-background-100/90 border border-primary-700/30 rounded-xl hover:border-primary-500/50 transition-colors"
            >
              <i className="ri-google-play-fill text-foreground-100 text-2xl" />
              <div className="text-left">
                <p className="text-foreground-500 text-[0.6rem] font-label leading-none">Get it on</p>
                <p className="text-foreground-100 text-sm font-label font-semibold leading-tight">Google Play</p>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}